import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Angulartics2Facebook, Angulartics2GoogleAnalytics, Angulartics2GoogleTagManager } from 'angulartics2';
import { CheckForUpdateService } from './core/ngsw/check-for-update.service';
import { UpdateService } from './core/ngsw/update.service';
import { CookieConsentService, COOKIES_CONSENT_KEY } from './core/cookie-consent/cookie-consent.service';
import { Store } from '@ngrx/store';
import * as fromRoot from './core/store/reducers';
import { SetCookieTrackingConsent } from './core/store/core-context/core-context-actions';
import { isCookieConsentGiven$ } from './core/store/core-context/core-context-helpers';
import { map } from 'rxjs/operators';
import { DMPAngularticsProviderService } from './core/tracking/dmpangulartics-provider.service';
import { setTheme } from 'ngx-bootstrap/utils';
import { combineLatest, Observable } from 'rxjs';
import { BaseParamsService } from '@app/core/base-params.service';
import { LoginLoadingService } from '@app/core/login-loading.service';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { RouterOutlet } from '@angular/router';
import { CookieConsentComponent } from './core/cookie-consent/cookie-consent.component';
import { SpinnerComponent } from './shared/branded-components/spinner/spinner.component';
import { AsyncPipe } from '@angular/common';
import { SettingsService } from '@app/core/settings/settings.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LoadingBarModule,
    RouterOutlet,
    CookieConsentComponent,
    SpinnerComponent,
    AsyncPipe,
  ],
})
export class AppComponent implements OnInit {
  private checkUpdateService = inject(CheckForUpdateService);
  private updateService = inject(UpdateService);
  private cookieConsentService = inject(CookieConsentService);
  private store = inject<Store<fromRoot.State>>(Store);
  private settingsService = inject(SettingsService);

  private readonly loginLoadingService = inject(LoginLoadingService);
  private readonly baseParamsService = inject(BaseParamsService);

  private readonly settingsLoaded = signal<boolean>(false);
  protected readonly loaded = computed(() => this.settingsLoaded() && !this.loginLoadingService.loginLoading());

  protected readonly isCookieBannerVisible$: Observable<boolean>;

  constructor() {
    const translate = inject(TranslateService);
    const ga = inject(Angulartics2GoogleAnalytics);
    const fb = inject(Angulartics2Facebook);
    const gtm = inject(Angulartics2GoogleTagManager);
    const dmpEvents = inject(DMPAngularticsProviderService);

    setTheme('bs5');
    gtm.startTracking();
    fb.startTracking();
    ga.startTracking();
    dmpEvents.startTracking();

    translate.use('en');

    this.isCookieBannerVisible$ = this.getIsCookeBannerVisible();
  }

  public ngOnInit(): void {
    this.baseParamsService.getParamsResolvedObservable().subscribe(() => {
      this.settingsLoaded.set(true);
      this.setupCookieConsentFromLocalStorage();
    });
    this.checkUpdateService.register();
    this.updateService.register();
  }

  public setupCookieConsentFromLocalStorage(): void {
    if (!this.cookieConsentService.isGiven()) { return; }

    this.store.dispatch(
      new SetCookieTrackingConsent(this.cookieConsentService.isApproved()),
    );
  }

  private getIsCookeBannerVisible(): Observable<boolean> {
    return combineLatest([
      isCookieConsentGiven$(this.store),
      this.baseParamsService.getParamsResolvedObservable(),
    ]).pipe(
      map(([isConsentGiven]) => {
        if (isConsentGiven) { return false; }

        if (this.baseParamsService.isInsideMobileAppWithToken()) {
          return false;
        }

        return this.settingsService.settings.schemaObject.consents.availableConsentsKeys()
          .includes(COOKIES_CONSENT_KEY);
      }),
    );
  }
}
